import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, X, Smartphone, ArrowUpFromLine, MoreVertical, Share2, Plus, Monitor } from "lucide-react";

/**
 * Umaang PWA install helper.
 *
 * Behavior:
 *  - On any browser that fires `beforeinstallprompt` (Android Chrome, Edge, desktop
 *    Chromium), we stash the event and show a floating "Install app" banner.
 *  - When user taps a manual install trigger (from Nav, Landing hero, or the banner),
 *    we call `deferred.prompt()` if we have one; otherwise we open an instructions
 *    dialog that explains how to install on the current platform (iOS Safari, Android
 *    Firefox, Samsung Internet, desktop Safari, etc.).
 *  - A small `window.__umaangInstall.open()` helper is exposed globally so *any*
 *    component (Nav menu item, hero CTA) can trigger the install without needing
 *    props / context.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [installed, setInstalled] = useState(false);

  // Detect current platform (used to render the right instructions)
  const platform = detectPlatform();

  const dismissBanner = useCallback(() => {
    setBannerVisible(false);
    localStorage.setItem("umg_install_dismissed_at", String(Date.now()));
  }, []);

  const openInstall = useCallback(async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* ignore */ }
      setDeferred(null);
      setBannerVisible(false);
      return;
    }
    // No native prompt available → show manual instructions
    setModalOpen(true);
  }, [deferred]);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) { setInstalled(true); return; }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Show floating banner unless dismissed in the last 30 days
      const at = Number(localStorage.getItem("umg_install_dismissed_at") || 0);
      if (!at || Date.now() - at > 30 * 24 * 60 * 60 * 1000) setBannerVisible(true);
    };
    const onInstalled = () => { setInstalled(true); setBannerVisible(false); };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // Expose a global hook so any button anywhere can trigger install
    window.__umaangInstall = { open: openInstall, hasNativePrompt: () => !!deferred };

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      delete window.__umaangInstall;
    };
  }, [openInstall, deferred]);

  // Refresh global hook when deferred changes
  useEffect(() => {
    if (window.__umaangInstall) {
      window.__umaangInstall.hasNativePrompt = () => !!deferred;
      window.__umaangInstall.open = openInstall;
    }
  }, [deferred, openInstall]);

  if (installed) return null;

  return (
    <>
      {/* Floating install banner */}
      {bannerVisible && (
        <div
          data-testid="pwa-install-prompt"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-[min(94vw,420px)] p-4 pr-3 rounded-2xl bg-gradient-to-br from-[#C85A53] via-[#DB2777] to-[#7C3AED] text-white shadow-2xl shadow-[#DB2777]/40 flex items-center gap-3"
        >
          <div className="w-11 h-11 rounded-xl bg-white overflow-hidden grid place-items-center shrink-0">
            <img src="/icon-192.png" alt="Umaang" className="w-full h-full object-contain" draggable="false" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold">Install Umaang app</div>
            <div className="text-[11px] opacity-90 mt-0.5">Faster access · push alerts · works offline</div>
          </div>
          <Button
            data-testid="pwa-install-btn"
            onClick={openInstall}
            className="btn-pill bg-white text-[#B91C1C] hover:bg-white/90 h-9 px-3 font-semibold"
          >
            <Download className="w-3.5 h-3.5 mr-1" /> Install
          </Button>
          <button
            data-testid="pwa-install-dismiss"
            onClick={dismissBanner}
            className="w-7 h-7 grid place-items-center rounded-full bg-white/15 hover:bg-white/25 transition shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Platform-specific instructions modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="pwa-install-modal" className="max-w-md p-0 overflow-hidden">
          <DialogTitle className="sr-only">Install Umaang app</DialogTitle>
          <DialogDescription className="sr-only">Steps to add Umaang to your device home screen</DialogDescription>
          <div className="bg-gradient-to-br from-[#C85A53] via-[#DB2777] to-[#7C3AED] p-6 text-white text-center">
            <div className="inline-block w-14 h-14 rounded-2xl bg-white overflow-hidden">
              <img src="/icon-192.png" alt="Umaang" className="w-full h-full object-contain" draggable="false" />
            </div>
            <div className="font-display text-2xl mt-3">Install Umaang</div>
            <div className="text-sm opacity-90 mt-1">Add Umaang to your home screen in 3 taps.</div>
          </div>
          <div className="p-6">
            <PlatformSteps platform={platform} />
            <button
              onClick={() => setModalOpen(false)}
              className="mt-6 w-full h-11 rounded-full bg-[#1C1917] text-white font-semibold"
              data-testid="pwa-modal-close"
            >
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * A reusable trigger button that works from anywhere. Renders nothing if the
 * app is already installed. Delegates to the global `window.__umaangInstall.open()`.
 */
export function InstallAppButton({ className = "", label = "Install App", variant = "default", testId = "install-app-btn" }) {
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) setVisible(false);
    const onInstalled = () => setVisible(false);
    window.addEventListener("appinstalled", onInstalled);
    return () => window.removeEventListener("appinstalled", onInstalled);
  }, []);
  if (!visible) return null;
  return (
    <button
      data-testid={testId}
      onClick={() => window.__umaangInstall?.open?.()}
      className={
        variant === "ghost"
          ? `inline-flex items-center gap-1.5 text-sm ${className}`
          : `inline-flex items-center gap-1.5 h-10 px-4 rounded-full text-sm font-semibold bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] text-white shadow-md shadow-[#DB2777]/30 hover:opacity-90 transition ${className}`
      }
    >
      <Download className="w-4 h-4" /> {label}
    </button>
  );
}

// -------- helpers --------

function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const isIOS = /iphone|ipad|ipod/i.test(ua) && !/crios|fxios|edgios/i.test(ua);
  const isIOSChrome = /crios/i.test(ua);
  const isIOSFirefox = /fxios/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isSamsung = /samsungbrowser/i.test(ua);
  const isFirefoxAndroid = isAndroid && /firefox/i.test(ua);
  const isChromeDesktop = /chrome|chromium|crios/i.test(ua) && !isAndroid && !isIOSChrome;
  const isEdge = /edg\//i.test(ua);
  const isSafariDesktop = /^((?!chrome|android|crios).)*safari/i.test(ua) && !isIOS;
  if (isIOS) return "ios";
  if (isIOSChrome) return "ios-chrome";
  if (isIOSFirefox) return "ios-firefox";
  if (isSamsung) return "samsung";
  if (isFirefoxAndroid) return "firefox-android";
  if (isAndroid) return "android";
  if (isEdge) return "edge";
  if (isChromeDesktop) return "chrome-desktop";
  if (isSafariDesktop) return "safari-desktop";
  return "unknown";
}

function Step({ n, icon: Icon, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 rounded-full bg-[#C85A53] text-white text-xs font-bold grid place-items-center shrink-0 mt-0.5">{n}</div>
      <div className="flex-1 text-sm text-[color:var(--umg-text)]">
        <div className="flex items-center gap-2">{text}</div>
      </div>
      {Icon && <Icon className="w-4 h-4 text-[color:var(--umg-text-2)] mt-1.5 shrink-0" />}
    </div>
  );
}

function PlatformSteps({ platform }) {
  switch (platform) {
    case "ios":
      return (
        <div className="space-y-3">
          <Step n={1} icon={Share2} text={<>Tap the <b>Share</b> icon at the bottom of Safari</>} />
          <Step n={2} icon={Plus} text={<>Scroll and tap <b>"Add to Home Screen"</b></>} />
          <Step n={3} text={<>Tap <b>Add</b> — Umaang appears on your home screen ✨</>} />
        </div>
      );
    case "ios-chrome":
    case "ios-firefox":
      return (
        <div className="space-y-3">
          <p className="text-sm text-[color:var(--umg-text-2)]">On iPhone / iPad, install works only via <b>Safari</b> browser.</p>
          <Step n={1} icon={Share2} text={<>Open Umaang in <b>Safari</b> (not this browser)</>} />
          <Step n={2} icon={Share2} text={<>Tap <b>Share</b> → <b>Add to Home Screen</b></>} />
        </div>
      );
    case "samsung":
      return (
        <div className="space-y-3">
          <Step n={1} icon={MoreVertical} text={<>Tap the <b>menu</b> (three lines) at the bottom right</>} />
          <Step n={2} icon={ArrowUpFromLine} text={<>Tap <b>"Add page to"</b> → <b>"Home screen"</b></>} />
          <Step n={3} text={<>Confirm — Umaang icon added to your home screen ✨</>} />
        </div>
      );
    case "firefox-android":
      return (
        <div className="space-y-3">
          <Step n={1} icon={MoreVertical} text={<>Tap the <b>three dots</b> menu (top right)</>} />
          <Step n={2} text={<>Tap <b>"Install"</b> or <b>"Add to Home Screen"</b></>} />
        </div>
      );
    case "android":
      return (
        <div className="space-y-3">
          <Step n={1} icon={MoreVertical} text={<>Tap the <b>three dots</b> menu (top right of Chrome)</>} />
          <Step n={2} icon={Download} text={<>Tap <b>"Install app"</b> or <b>"Add to Home Screen"</b></>} />
          <Step n={3} text={<>Umaang icon appears on your home screen ✨</>} />
          <p className="text-[11px] text-[color:var(--umg-text-2)] pt-1">Not seeing "Install app"? Refresh the page and browse a bit — Chrome shows it after a short engagement.</p>
        </div>
      );
    case "chrome-desktop":
    case "edge":
      return (
        <div className="space-y-3">
          <Step n={1} icon={Download} text={<>Look for the <b>Install app</b> icon <span className="inline-block px-1 py-0.5 border rounded ml-1"><Monitor className="inline w-3 h-3" /></span> at the right end of the address bar</>} />
          <Step n={2} text={<>Click it → then <b>Install</b></>} />
          <Step n={3} text={<>Or use the browser menu → <b>"Install Umaang…"</b></>} />
        </div>
      );
    case "safari-desktop":
      return (
        <div className="space-y-3">
          <Step n={1} icon={Share2} text={<>Click <b>File</b> menu → <b>Add to Dock</b> (macOS Sonoma+)</>} />
          <Step n={2} text={<>Or click the <b>Share</b> button → <b>Add to Dock</b></>} />
        </div>
      );
    default:
      return (
        <div className="space-y-3">
          <Step n={1} icon={MoreVertical} text={<>Open your browser's <b>menu</b> (three dots or lines)</>} />
          <Step n={2} icon={Download} text={<>Look for <b>"Install app"</b> or <b>"Add to Home Screen"</b></>} />
          <p className="text-[11px] text-[color:var(--umg-text-2)] pt-1">Different browsers have slightly different steps — but every modern browser can install Umaang.</p>
        </div>
      );
  }
}
